import { FieldValues } from "react-hook-form";
import {
  AfrrFormValues,
  JboxFormValues,
  JBoxTypeKey,
  RampTestKey,
  RampTestValues,
  SocManagementFormValues,
} from "./types";

interface SocManagementErrors {
  duration?: string;
  target_soc?: Record<string, string>;
  active?: boolean;
}

interface AfrrErrors {
  initial_soc_restoration_power?: Record<string, string>;
  afrr_engagement_up?: Record<string, string>;
  afrr_engagement_down?: Record<string, string>;
  reactivity_delay?: string;
}

interface RampTestErrors {
  plannings_soc_management?: SocManagementErrors;
  plannings_afrr_phase1?: AfrrErrors;
  plannings_afrr_phase2?: AfrrErrors;
  chronicle_filename?: string;
}

interface FormErrors {
  group_id?: string;
  startingDate?: string;
  rampTest1?: RampTestErrors;
  rampTest2?: RampTestErrors;
}

const validateGroupId = (groupId: string, errors: FormErrors) => {
  if (!groupId) {
    errors.group_id = "Group is required";
  }
};

const validateStartingDate = (startingDate: Date, errors: FormErrors) => {
  // start date is required, cannot be in the past, can only be a multiple of 5 minutes
  if (!startingDate) {
    errors.startingDate = "Start date is required";
  } else if (startingDate < new Date()) {
    errors.startingDate = "Start date cannot be in the past";
  } else if (startingDate.getMinutes() % 5 !== 0) {
    errors.startingDate = "Start date must be a multiple of 5 minutes";
  }
};

const checkIfAtLeastOneValueProvided = (values: JboxFormValues, existingJboxTypes?: JBoxTypeKey[]) => {
  if (existingJboxTypes) {
    return existingJboxTypes.some((jboxType) => values[jboxType] !== null);
  }
  return Object.values(values).some((value) => value !== null);
};

const generateJboxErrorMessageObject = (key: string, existingJboxTypes?: JBoxTypeKey[]) => {
  const errorMessage = `At least one ${key} must be provided`;
  if (existingJboxTypes) {
    const errorObj: Record<string, string> = {};
    existingJboxTypes.forEach((jboxType) => {
      errorObj[jboxType] = errorMessage;
    });
    return errorObj;
  }
  return {
    [JBoxTypeKey.JBox8Racks]: errorMessage,
    [JBoxTypeKey.JBox10Racks]: errorMessage,
    [JBoxTypeKey.JBox2]: errorMessage,
  };
};

const validateSocManagement = (
  socManagementValues: SocManagementFormValues,
  key: RampTestKey,
  errors: FormErrors,
  existingJboxTypes?: JBoxTypeKey[],
) => {
  if (!socManagementValues?.active) return;

  const socManagementErrors: SocManagementErrors = {};

  const hasDuration = socManagementValues.duration !== undefined && socManagementValues.duration !== null;

  if (hasDuration) {
    const d = socManagementValues.duration as number;
    if (d <= 0) {
      socManagementErrors.duration = "Duration must be greater than 0";
    } else if (d % 5 !== 0) {
      socManagementErrors.duration = "Duration must be a multiple of 5 minutes";
    }
  }

  const atLeastOneProvided = checkIfAtLeastOneValueProvided(socManagementValues.target_soc, existingJboxTypes);
  if (!atLeastOneProvided) {
    socManagementErrors.target_soc = generateJboxErrorMessageObject("Initial SoC", existingJboxTypes);
  } else {
    const jboxTypesToCheck = existingJboxTypes || (Object.keys(socManagementValues.target_soc) as JBoxTypeKey[]);
    jboxTypesToCheck.forEach((jbox) => {
      const value = socManagementValues.target_soc[jbox];
      if (value !== null && (value < 0 || value > 1)) {
        if (!socManagementErrors.target_soc) socManagementErrors.target_soc = {};
        socManagementErrors.target_soc[jbox] = "Value must be between 0 and 1";
      }
    });
  }

  if (Object.keys(socManagementErrors).length) {
    if (!errors[key]) errors[key] = {};
    (errors[key] as RampTestErrors).plannings_soc_management = socManagementErrors;
  }
};

const validateAfrr = (
  afrrValues: AfrrFormValues,
  key: RampTestKey,
  errors: FormErrors,
  phaseKey: "plannings_afrr_phase1" | "plannings_afrr_phase2",
  afrrActive: boolean,
  existingJboxTypes?: JBoxTypeKey[],
) => {
  if (!afrrActive) return;

  const afrrErrors: AfrrErrors = {};

  const atLeastInitial = afrrValues.initial_soc_restoration_power
    ? checkIfAtLeastOneValueProvided(afrrValues.initial_soc_restoration_power, existingJboxTypes)
    : false;
  const atLeastUp = afrrValues.afrr_engagement_up
    ? checkIfAtLeastOneValueProvided(afrrValues.afrr_engagement_up, existingJboxTypes)
    : false;
  const atLeastDown = afrrValues.afrr_engagement_down
    ? checkIfAtLeastOneValueProvided(afrrValues.afrr_engagement_down, existingJboxTypes)
    : false;

  if (!atLeastInitial) {
    afrrErrors.initial_soc_restoration_power = generateJboxErrorMessageObject(
      "Initial SoC restoration power",
      existingJboxTypes,
    );
  }
  if (!atLeastUp) {
    afrrErrors.afrr_engagement_up = generateJboxErrorMessageObject("AFRR engagement up", existingJboxTypes);
  }
  if (!atLeastDown) {
    afrrErrors.afrr_engagement_down = generateJboxErrorMessageObject("AFRR engagement down", existingJboxTypes);
  }

  const validateReactivityDelay = afrrValues.reactivity_delay;
  if (validateReactivityDelay === undefined || validateReactivityDelay === null) {
    afrrErrors.reactivity_delay = "Reactivity delay is required";
  } else if (validateReactivityDelay < 0 || validateReactivityDelay > 1500) {
    afrrErrors.reactivity_delay = "Value must be between 0 and 1500 milliseconds";
  }

  const jboxTypesToCheck =
    existingJboxTypes ||
    (afrrValues.initial_soc_restoration_power
      ? (Object.keys(afrrValues.initial_soc_restoration_power) as JBoxTypeKey[])
      : []);
  jboxTypesToCheck.forEach((jbox) => {
    if (afrrValues.initial_soc_restoration_power) {
      const validateSocResto = afrrValues.initial_soc_restoration_power[jbox];
      if (validateSocResto !== null && (validateSocResto < -1.25 || validateSocResto > 1.25)) {
        if (!afrrErrors.initial_soc_restoration_power) afrrErrors.initial_soc_restoration_power = {};
        afrrErrors.initial_soc_restoration_power[jbox] = "Value must be between -1.25 and 1.25";
      }
    }
    if (afrrValues.afrr_engagement_up) {
      const validateEngagementUp = afrrValues.afrr_engagement_up[jbox];
      if (validateEngagementUp !== null && validateEngagementUp < 0) {
        if (!afrrErrors.afrr_engagement_up) afrrErrors.afrr_engagement_up = {};
        afrrErrors.afrr_engagement_up[jbox] = "Value must be greater than 0";
      }
    }
    if (afrrValues.afrr_engagement_down) {
      const validateEngagementDown = afrrValues.afrr_engagement_down[jbox];
      if (validateEngagementDown !== null && validateEngagementDown < 0) {
        if (!afrrErrors.afrr_engagement_down) afrrErrors.afrr_engagement_down = {};
        afrrErrors.afrr_engagement_down[jbox] = "Value must be greater than 0";
      }
    }
  });

  if (Object.keys(afrrErrors).length) {
    if (!errors[key]) errors[key] = {};
    (errors[key] as any)[phaseKey] = afrrErrors;
  }
};

/** same as before, but with a flag that tells us if the IEC list is available */
const validateRampTest = (
  {
    plannings_soc_management,
    plannings_afrr_phase1,
    plannings_afrr_phase2,
    afrr_active,
    chronicle_filename,
  }: RampTestValues,
  key: RampTestKey,
  errors: FormErrors,
  existingJboxTypes: JBoxTypeKey[] | undefined,
  iecAvailable: boolean,
) => {
  // SoC controlled by its own toggle
  validateSocManagement(plannings_soc_management, key, errors, existingJboxTypes);

  // AFRR controlled by shared toggle
  validateAfrr(plannings_afrr_phase1, key, errors, "plannings_afrr_phase1", Boolean(afrr_active), existingJboxTypes);
  validateAfrr(plannings_afrr_phase2, key, errors, "plannings_afrr_phase2", Boolean(afrr_active), existingJboxTypes);

  // Only require filename when AFRR is active AND IEC list is available
  if (afrr_active && iecAvailable && !chronicle_filename) {
    if (!errors[key]) errors[key] = {};
    (errors[key] as RampTestErrors).chronicle_filename = "AFRR filename is required when AFRR is active";
  }
};

/** create a validate function bound to current IEC availability */
export const makeValidateForm = (iecAvailable: boolean) => (values: FieldValues) => {
  const { group_id, startingDate, rampTest1, rampTest2 } = values;
  const errors: FormErrors = {};
  validateGroupId(group_id, errors);
  validateStartingDate(startingDate, errors);
  validateRampTest(rampTest1, RampTestKey.RampTest1, errors, undefined, iecAvailable);
  validateRampTest(rampTest2, RampTestKey.RampTest2, errors, undefined, iecAvailable);
  return errors;
};

/** same as before, but with a flag that tells us if the IEC list is available */
export const makeValidateFormWithSites =
  (iecAvailable: boolean, existingJboxTypes: JBoxTypeKey[]) => (values: FieldValues) => {
    const { group_id, startingDate, rampTest1, rampTest2 } = values;
    const errors: FormErrors = {};
    validateGroupId(group_id, errors);
    validateStartingDate(startingDate, errors);
    validateRampTest(rampTest1, RampTestKey.RampTest1, errors, existingJboxTypes, iecAvailable);
    validateRampTest(rampTest2, RampTestKey.RampTest2, errors, existingJboxTypes, iecAvailable);
    return errors;
  };
